/* @flow */
import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { translate } from 'react-i18next';
import moment from 'moment';

import type { TranslatorProps } from 'react-i18next';

import type TypeActionDrawerStore from 'stores/ActionDrawerStore';
import ContractStore from 'stores/ContractStore';

import Form from 'forms/_.extend';
import Loading from 'components/Loading/Loading';
import ActionDrawerHeader from 'views/ActionDrawer/components/ActionDrawerHeader/ActionDrawerHeader';

import ExtendContractForm from './ExtendContractForm/ExtendContractForm';
import ExtendContractSuccess from './ExtendContractSuccess/ExtendContractSuccess';
import ExtendContractError from './ExtendContractError/ExtendContractError';

import { isAdmin } from '../../.././../../permissions/general.js';

import styles from '../../../ActionDrawer.module.scss';

type TypeProps = {|
  actionDrawerStore: TypeActionDrawerStore,
  contractStore: ContractStore,
  contract: Object,
|} & TranslatorProps;

@translate()
@inject('contractStore', 'actionDrawerStore')
@observer
export default class ExtendContract extends React.Component<*> {
  createForm: Function = () => {
    const { t, actionDrawerStore, contract }: TypeProps = this.props;

    let currentEndDate: Date = new Date();
    currentEndDate.setDate(currentEndDate.getDate() - 1);

    if (contract.er_expected_end_date) {
      const contractEndDate = moment(contract.er_expected_end_date, 'YYYY-MM-DD').toDate();

      if (contractEndDate > currentEndDate) {
        currentEndDate = contractEndDate;
      }
    }

    const fields: Array<Object> = [
      {
        name: 'extendAssets',
        label: t('general:actions.select_assets_to_extend'),
        fields: [
          {
            name: 'assets',
            label: t('general:asset_plural'),
            placeholder: t('general:placeholders.asset_manufacturer_model'),
            rules: 'required|min:1|array',
            value: [],
          },
        ],
      },
      {
        name: 'extendDetails',
        label: t('general:reasons.when_extend_till'),
        fields: [
          {
            name: 'endDate',
            label: t('general:labels.new_end_date'),
            rules: `required|date|after:${moment(currentEndDate).format('MMMM DD YYYY')}`,
            value: new Date(
              moment(currentEndDate)
                .add(1, 'd')
                .format('YYYY-MM-DD')
            ),
          },
          {
            name: 'reason',
            label: t('general:reasons.reason_for_extending_contract'),
            rules: 'required|string',
            value: '',
            extra: {
              options: [
                {
                  id: '1',
                  label: t('general:reasons.options.assets_still_required'),
                },
                {
                  id: '2',
                  label: t('general:reasons.options.owned_assets_unavailable'),
                },
                {
                  id: '3',
                  label: t('general:reasons.options.owned_assets_require_repairs'),
                },
                {
                  id: '4',
                  label: t('general:reasons.options.owned_assets_require_calibration'),
                },
                {
                  id: 'other',
                  label: t('general:reasons.options.other'),
                },
              ],
            },
          },
          {
            name: 'newPONumber',
            label: t('general:labels.new_po_number'),
          },
        ],
      },
    ];

    const hooks: Object = {
      onSuccess(data: Object) {
        const { reason, endDate, newPONumber }: Object = data.values().extendDetails;

        const { assets }: Object = data.values().extendAssets;

        actionDrawerStore.extendContract(contract.id, {
          end_date: endDate,
          reason,
          new_po_number: newPONumber,
          asset: assets,
        });
      },
    };

    return new Form(
      { fields },
      {
        hooks,
        name: 'extendContract',
      }
    );
  };

  render(): React.Node {
    const { t, actionDrawerStore, contract }: TypeProps = this.props;

    const title: string = isAdmin()
      ? t('general:actions.extend_contract')
      : t('general:actions.extend_contract_request');

    if (actionDrawerStore.loading) {
      return <Loading />;
    }

    if (actionDrawerStore.error) {
      return (
        <div className={`${styles['action-drawer-form']} ${styles.error}`}>
          <ActionDrawerHeader title={title} />
          <ExtendContractError contract={contract} />
        </div>
      );
    }

    if (actionDrawerStore.success) {
      return (
        <div className={`${styles['action-drawer-form']} ${styles.success}`}>
          <ActionDrawerHeader title={title} />
          <ExtendContractSuccess response={actionDrawerStore.response} />
        </div>
      );
    }

    return (
      <div className={styles['action-drawer-form']}>
        <ActionDrawerHeader title={title} />
        <ExtendContractForm contract={contract} form={this.createForm()} />
      </div>
    );
  }
}
